import React, {Fragment, useCallback, useMemo} from "react";
import {atom, useRecoilState, useRecoilValue, useResetRecoilState} from "recoil";
import {Dialog, Transition} from "@headlessui/react";
import classNames from "classnames";


export const modalStatusState = atom<{[id: string]: boolean}>({
    key: 'modalStatus',
    default: {}
})

const modalDataState = atom<{[id: string]: any}>({
    key: 'modalData',
    default: {}
})

const useModal = () => {
    const [modalStatus, setModalStatus] = useRecoilState(modalStatusState)
    const [modalData, setModalData] = useRecoilState(modalDataState)

    const resetModalStatus = useResetRecoilState(modalStatusState)
    const resetModalData = useResetRecoilState(modalDataState)

    const register = useCallback((id: string, status?: boolean) => {
        setModalStatus({...modalStatus, [id]: !!status})
    }, [modalStatus, setModalStatus])

    const open = useCallback((id: string, data?: any) => {
        if (modalStatus[id] === undefined) {
            register(id)
        }

        setModalStatus(prev => ({
            ...prev,
            [id]: true
        }))

        setModalData(prev => ({
            ...prev,
            [id]: data
        }))
    }, [modalStatus, setModalStatus, setModalData, register])

    const close = useCallback((id: string) => {
        setModalStatus(prev => ({
            ...prev,
            [id]: false
        }))

        setModalData(prev => ({
            ...prev,
            [id]: null
        }))
    }, [setModalStatus, setModalData])

    const closeThenOpen = useCallback((closeId: string, openId: string, data?: any) => {
        if (modalStatus[openId] === undefined) {
            register(openId)
        }

        setModalStatus(prev => ({
            ...prev,
            [closeId]: false,
            [openId]: true
        }))

        setModalData(prev => ({
            ...prev,
            [closeId]: null,
            [openId]: data
        }))
    }, [modalStatus, setModalStatus, setModalData, register])

    const clear = useCallback(() => {
        resetModalStatus()
        resetModalData()
    }, [resetModalStatus, resetModalData])

    return useMemo(() => {
        return {
            register,
            open,
            close,
            closeThenOpen,
            clear,
            data: modalData,
            status: modalStatus
        }
    }, [register, open, close, clear, modalData, modalStatus, closeThenOpen])
}

export const Modal: React.FC<ModalProps> = ((props) => {
    const {children, id, title, onClose, showCloseButton = true, customCloseButton, className, customSize,  size = "lg", side = false} = props

    const modal = useModal();
    const modalStatus = useRecoilValue(modalStatusState)

    const handleClose = () => {
        onClose?.();
        modal.close(id);
    };

    const panelClassName = useMemo(() => {
        const commonClassName = classNames(
            "transform overflow-hidden bg-white shadow-xl transition-all",
            !side && "rounded-xl p-6"
        );
        let width = "";
        switch (size) {
            case "sm":
                width = "w-[400px]"
                break
            case "md":
                width = "w-[520px]"
                break
            case "slg":
                width = "w-[600px]"
                break
            case "lg":
                width = "w-[700px]"
                break
            case "sxl":
                width = "w-[850px]"
                break
            case "xl":
                width = "w-[1000px]"
                break
            case "2xl":
                width = "w-[1200px]"
                break
            case "full":
                width = "w-screen h-screen rounded-none absolute"
                break
        }
        if (customSize) {
            width = customSize
        }
        return classNames(commonClassName, className, width);
    }, [className, size, customSize, side]);

    const transition = useMemo(() => {
        const backgroundTransition = {
            enter: "transition-opacity ease-out duration-300",
            enterFrom: "opacity-0",
            enterTo: "opacity-100",
            leave: "transition-opacity ease-in duration-200",
            leaveFrom: "opacity-100",
            leaveTo: "opacity-0"
        };


        if (side) {
            return {
                background: backgroundTransition,
                panel: {
                    enter: "ease-out duration-300",
                    enterFrom: "opacity-0",
                    enterTo: "opacity-100",
                    leave: "ease-in duration-200",
                    leaveFrom: "opacity-100",
                    leaveTo: "opacity-0"
                }
            }
        } else {
            return {
                background: backgroundTransition,
                panel: {
                    enter: "ease-out duration-300",
                    enterFrom: "opacity-0 scale-95",
                    enterTo: "opacity-100 scale-100",
                    leave: "ease-in duration-200",
                    leaveFrom: "opacity-100 scale-100",
                    leaveTo: "opacity-0 scale-95"
                }
            }
        }

    }, [side])

    return (
        <Transition appear show={modalStatus[id] ?? false} as={Fragment}>
            <Dialog as="div" className="relative z-30" onClose={handleClose}>
                <Transition.Child as={Fragment}
                                  enter={transition.background.enter}
                                  enterFrom={transition.background.enterFrom}
                                  enterTo={transition.background.enterTo}
                                  leave={transition.background.leave}
                                  leaveFrom={transition.background.leaveFrom}
                                  leaveTo={transition.background.leaveTo}
                >
                    {/*TODO dialog dim 배경 디자이너분과 얘기해보기*/}
                    <div className={classNames(
                        "fixed inset-0 bg-black",
                        side ? "bg-opacity-60" : "bg-opacity-25"
                    )}/>
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className={classNames(
                        side ?
                            "flex min-h-full justify-end"
                            :
                            "flex min-h-full items-center justify-center p-4",
                    )}>
                        <Transition.Child as={Fragment}
                                          enter={transition.panel.enter}
                                          enterFrom={transition.panel.enterFrom}
                                          enterTo={transition.panel.enterTo}
                                          leave={transition.panel.leave}
                                          leaveFrom={transition.panel.leaveFrom}
                                          leaveTo={transition.panel.leaveTo}
                        >
                            <Dialog.Panel className={panelClassName}>
                                <Dialog.Title as="h3"
                                              className="text-lg font-bold leading-6 text-gray-900 relative z-30">
                                    {title}
                                    {showCloseButton && (
                                        <div onClick={handleClose}>
                                            {customCloseButton ?? <i className="absolute fa-solid fa-close right-0 top-0 cursor-pointer"/>}
                                        </div>
                                    )}
                                </Dialog.Title>
                                {children}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
});

interface ModalProps {
    id: string;
    title?: React.ReactNode;
    onClose?: () => void;
    showCloseButton?: boolean;
    customCloseButton?: React.ReactNode;
    children?: React.ReactNode;
    className?: string;
    size?: "sm" | "md" | "slg" | "lg" | "sxl" | "xl" | "2xl" | "full";
    customSize?: string;
    side?: boolean;
}

export default useModal;
